/* eslint-disable @typescript-eslint/no-magic-numbers */
import { Tooth, ToothPerioDraft, ToothSide } from '@libs/ui-lib';
import { Repeat } from '@libs/utils';
import { Provider } from '@ffd/api-client';
import { PerioTooltipToothSurface } from '../../../perio-add-treatment-screen/perio-exam-toolbar/perio-exam-toolbar.interface';
import { PerioJawConfig } from '../../../perio-jaw/perio-jaw.interface';

export type PerioExamTraversalStrategy = 'archWise' | 'buccalThenLingual' | 'crossArchSnake' | 'crossArchSnakeReverse';
export type RecessionInputType = 'central' | 'all';

export interface PerioExamTraversalStrategyStep {
  side: ToothSide;
  position: number;
}

// eslint-disable-next-line @typescript-eslint/no-magic-numbers
export type PerioJawConfigTuple = Repeat<PerioJawConfig, 4>;
export type PerioExamDirectionJawConfigMap = Record<PerioExamTraversalStrategy, PerioJawConfigTuple>;

export type PerioExamDirectionStrategyOrder = Record<PerioExamTraversalStrategy, PerioExamTraversalStrategyStep[]>;

export type PerioTeethRecord = Record<number, ToothPerioDraft>;

export type PerioExamToothWithPosition<T extends string = 'current'> = {
  [K in `${T}${'Tooth' | 'ToothPosition' | 'ToothIndex'}`]: K extends `${T}Tooth`
    ? Tooth
    : K extends `${T}ToothIndex`
      ? number
      : PerioExamTraversalStrategyStep;
};

export interface PerioExamStateBase {
  loaded: boolean;
  teethRecord: PerioTeethRecord;
  completedSteps: PerioExamTraversalStrategyStep[];
  touchedSteps: PerioExamTraversalStrategyStep[];
  extractedTeethPositions: number[];
  missingTeethPositions: number[];
  currentToothPosition: PerioExamTraversalStrategyStep;
  traversalStrategy: PerioExamTraversalStrategy;
  toothOrder: PerioExamTraversalStrategyStep[];
  providerId: string | undefined | null;
  cheatSheetOpened: boolean;
  isEditing: boolean;
  recessionInputType: RecessionInputType;
}

// Instead of defining multiple interfaces for editing and non-editing states, we just make a union type that handles the
// mandatory fields based on the isEditing flag
export type PerioExamState =
  | (PerioExamStateBase & {
      isEditing: true;
      originalExamData: number;
    })
  | (PerioExamStateBase & {
      isEditing: false;
      originalExamData?: number;
    });

export interface PerioExamViewModel {
  loaded: boolean;
  teeth: Tooth[];
  teethRecord: PerioTeethRecord;
  currentToothPosition: PerioExamTraversalStrategyStep;
  previousToothPosition: PerioExamTraversalStrategyStep;
  nextToothPosition: PerioExamTraversalStrategyStep;
  traversalStrategy: PerioExamTraversalStrategy;
  jawConfig: PerioJawConfig;
  indicatorToothSurfaceList: PerioTooltipToothSurface[];
  currentTooth: Tooth;
  currentToothForJawVisualisation: Tooth;
  previousToothForJawVisualisation: Tooth | null;
  nextToothForJawVisualisation: Tooth | null;
  isFirstToothActive: boolean;
  isLastToothActive: boolean;
  /** Absolute first step in full traversal order (position+side). */
  isFirstToothInExam: boolean;
  /** Absolute last step in full traversal order (position+side). */
  isLastToothInExam: boolean;
  provider: Provider | undefined;
  profileId: string | undefined;
  extractedTeethPositions: number[];
  missingTeethPositions: number[];
  allTeethTouched: boolean;
  oneToothTouched: boolean;
  addPerioExamFeatureEnabled: boolean;
  editPerioExamFeatureEnabled: boolean;
  cheatSheetOpened: boolean;
  isEditing: boolean;
  recessionInputType: RecessionInputType;
}

export const TOOTH_POSITIONS_WITH_FG: readonly number[] = [1, 2, 3, 5, 12, 14, 15, 16, 17, 18, 19, 30, 31, 32];
export const TOOTH_POSITIONS_WITH_FG_AS_MESIAL_AND_DISTAL: readonly number[] = [5, 12];

export enum Side {
  Buccal = 'buccal',
  Lingual = 'lingual',
}

export enum Surface {
  Distal = 'distal',
  Central = 'central',
  Mesial = 'mesial',
}

// Step interfaces for navigation actions

export interface GoToToothInstr {
  type: 'goto-tooth';
  tooth: number | null;
  relative: boolean;
  surface: Surface | null;
}

export interface SkipToothInstr {
  type: 'skip-tooth';
  tooth_index: number;
}

export interface GoToSideInstr {
  type: 'goto-side';
  side: 'buccal' | 'lingual';
}

export interface MissingToothInstr {
  type: 'missing-tooth';
  tooth: number;
}

export interface MeasurementInstr {
  type: 'measurement';
  depth: number | null;
  bop?: boolean | null;
  suppuration?: boolean | null;
  mobility: number | null;
  furcation: number | null;
  recession: number | null;
}

export interface DataInputModeInstr {
  type: 'data-input-mode';
  mode: 'pocket_depth' | 'recession';
}

export type MeasurementField = 'depth' | 'bop' | 'suppuration' | 'mobility' | 'furcation' | 'recession';

export interface ClearInstr {
  type: 'clear';
  tooth: number | null;
  side: 'buccal' | 'lingual' | 'both' | null;
  surfaces: Surface[] | null;
  measurements: MeasurementField[] | null;
}

export interface MeasurementAddendumInstr {
  type: 'measurement-addendum';
  tooth: number | null;
  side: 'buccal' | 'lingual' | null;
  surfaces: Surface[] | null;
  depth: number | null;
  bop?: boolean | null;
  suppuration?: boolean | null;
  mobility: number | null;
  furcation: number | null;
  recession: number | null;
}

// Union type for all steps
export type Instruction =
  | SkipToothInstr
  | GoToSideInstr
  | MeasurementInstr
  | GoToToothInstr
  | MissingToothInstr
  | ClearInstr
  | MeasurementAddendumInstr
  | DataInputModeInstr;

// Main data model for AI responses
export interface DataModel {
  instructions: Instruction[];
  request_more_input: boolean;
}

export interface PeriodontalMeasurement {
  tooth: number; // 1–32
  surface: 'D' | 'C' | 'M'; // Distal, Central, Mesial
  depth: number | null; // 1–9
  bleeding: boolean;
  suppuration: boolean;
}
